import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { TrendingUp, TrendingDown, Wallet, Users, Plane, AlertCircle, DollarSign } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { formatBRL } from '@/lib/format';
import { useAuth } from '@/context/AuthContext';
import VendorDashboard from '@/pages/erp/VendorDashboard';

const axisStyle = { fill: 'hsl(var(--muted-foreground))', fontSize: 12 };

function KpiCard({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${accent || 'bg-primary/10 text-primary'}`}><Icon className="h-4 w-4" /></span>
      </div>
      <div className="mt-3 font-display text-2xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </Card>
  );
}

function ChartCard({ title, children }) {
  return (
    <Card className="p-5">
      <h3 className="font-semibold mb-4">{title}</h3>
      <div className="h-72">{children}</div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isManager = ['admin', 'supervisor'].includes(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Vendedor vê o painel pessoal — early return evita o fetch de manager
  if (!isManager) return <VendorDashboard />;

  return <ManagerDashboard data={data} loading={loading} setData={setData} setLoading={setLoading} />;
}

function ManagerDashboard({ data, loading, setData, setLoading }) {
  useEffect(() => {
    api.get('/finance/dashboard').then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-muted-foreground">Carregando indicadores...</p>;
  if (!data) return <p className="text-muted-foreground">Não foi possível carregar o painel.</p>;
  const k = data.kpis;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={DollarSign} label="Receita total" value={formatBRL(k.receita_total)} sub={`${k.total_vendas} vendas`} testid="kpi-receita" />
        <KpiCard icon={TrendingDown} label="Custos" value={formatBRL(k.custo_total)} accent="bg-[hsl(16_70%_55%/0.12)] text-[hsl(16_70%_45%)]" testid="kpi-custo" />
        <KpiCard icon={TrendingUp} label="Lucro" value={formatBRL(k.lucro_total)} accent="bg-[hsl(174_55%_34%/0.15)] text-[hsl(174_55%_28%)]" testid="kpi-lucro" />
        <KpiCard icon={AlertCircle} label="A receber (vencido)" value={formatBRL(k.a_receber_vencido)} sub={`${formatBRL(k.a_receber)} no total`} accent="bg-destructive/10 text-destructive" testid="kpi-areceber" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Wallet} label="A pagar" value={formatBRL(k.a_pagar)} testid="kpi-apagar" />
        <KpiCard icon={DollarSign} label="Recebido" value={formatBRL(k.recebido_total)} accent="bg-[hsl(174_55%_34%/0.15)] text-[hsl(174_55%_28%)]" testid="kpi-recebido" />
        <KpiCard icon={Users} label="Clientes" value={k.total_clientes} testid="kpi-clientes" />
        <KpiCard icon={Plane} label="Viagens" value={k.total_viagens} testid="kpi-viagens" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Receita vs Custos">
          {data.receita_vs_custo.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sem dados ainda</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.receita_vs_custo}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="4 4" />
                <XAxis dataKey="mes" tick={axisStyle} /><YAxis tick={axisStyle} width={70} />
                <Tooltip formatter={(v) => formatBRL(v)} contentStyle={{ borderRadius: 12, border: '1px solid hsl(var(--border))' }} />
                <Legend />
                <Line type="monotone" dataKey="receita" name="Receita" stroke="hsl(var(--chart-1))" strokeWidth={2} />
                <Line type="monotone" dataKey="custo" name="Custo" stroke="hsl(var(--chart-5))" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Fluxo de caixa">
          {data.fluxo_caixa.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sem lançamentos pagos ainda</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.fluxo_caixa}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="4 4" />
                <XAxis dataKey="mes" tick={axisStyle} /><YAxis tick={axisStyle} width={70} />
                <Tooltip formatter={(v) => formatBRL(v)} contentStyle={{ borderRadius: 12, border: '1px solid hsl(var(--border))' }} />
                <Legend />
                <Bar dataKey="entradas" name="Entradas" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="saidas" name="Saídas" fill="hsl(var(--chart-5))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Custos por fornecedor">
          {data.custos_por_fornecedor.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sem dados ainda</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.custos_por_fornecedor} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="4 4" />
                <XAxis type="number" tick={axisStyle} /><YAxis type="category" dataKey="fornecedor" tick={axisStyle} width={100} />
                <Tooltip formatter={(v) => formatBRL(v)} contentStyle={{ borderRadius: 12, border: '1px solid hsl(var(--border))' }} />
                <Bar dataKey="custo" name="Custo" fill="hsl(var(--chart-1))" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <Card className="p-5">
          <h3 className="font-semibold mb-4">Lucro por viagem</h3>
          {data.lucro_por_viagem.length === 0 ? (
            <div className="h-60 flex items-center justify-center text-sm text-muted-foreground">Nenhuma venda registrada ainda</div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {data.lucro_por_viagem.map((t) => (
                <div key={t.trip_id} className="flex items-center justify-between gap-3 py-2 border-b border-border/50 last:border-0">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{t.nome}</div>
                    <div className="text-xs text-muted-foreground truncate">{t.destino}</div>
                  </div>
                  <Badge variant="secondary" className={`tabular-nums ${t.lucro >= 0 ? 'text-[hsl(174_55%_24%)]' : 'text-destructive'}`}>{formatBRL(t.lucro)}</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-5" data-testid="dashboard-valores-fornecedor">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Valores por fornecedor</h3>
          <span className="text-sm text-muted-foreground">Total: <span className="font-semibold text-foreground tabular-nums">{formatBRL((data.valores_por_fornecedor || []).reduce((s, v) => s + (v.valor || 0), 0))}</span></span>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Soma dos valores pagos direcionados a cada fornecedor.</p>
        {(!data.valores_por_fornecedor || data.valores_por_fornecedor.length === 0) ? (
          <div className="h-24 flex items-center justify-center text-sm text-muted-foreground">Nenhum pagamento registrado ainda</div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {data.valores_por_fornecedor.map((v, i) => (
              <div key={i} className="flex items-center justify-between gap-3 py-2 border-b border-border/50 last:border-0" data-testid={`valor-fornecedor-${i}`}>
                <span className="text-sm font-medium truncate">{v.fornecedor}</span>
                <Badge variant="secondary" className="tabular-nums text-[hsl(174_55%_24%)]">{formatBRL(v.valor)}</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
